/* Facturation des commissions : ce qu'Omnix facture aux usines, et ce que les
   agents refacturent à Omnix. Réservé à la direction. */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgents, useCommissions, useUsines, useVentes } from "@/lib/donnees";
import { useNiveau } from "@/lib/session";
import { Empty, Kpi, Kpis, Section, Tag } from "@/ui/primitives";
import { Table, dansUnChamp } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { dateFr, euro, euroCourt } from "@/lib/format";
import {
  emettreFacture, emettreToutes, lireSerie, marquerReglee, numeroSuivant, recevoirFactureAgent,
} from "./actions";
import type { SerieFacture } from "./actions";
import type { Commission } from "@/types/metier";

export function Facturation() {
  const navigue = useNavigate();
  const toast = useToast();
  const niveau = useNiveau();
  const { donnees: commissions, chargement, erreur, recharger } = useCommissions();
  const { donnees: usines } = useUsines();
  const { donnees: agents } = useAgents();
  const { donnees: ventes } = useVentes();
  const [serie, setSerie] = useState<SerieFacture | null>(null);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    void lireSerie().then(setSerie);
  }, []);

  const nomUsine = useMemo(() => new Map(usines.map((u) => [u.id, u.nom] as const)), [usines]);
  const nomAgent = useMemo(() => new Map(agents.map((a) => [a.id, a.nom] as const)), [agents]);
  const refVente = useMemo(
    () => new Map(ventes.map((v) => [v.id, v.reference] as const)),
    [ventes],
  );
  const factureUsineDe = useMemo(
    () => new Map(ventes.map((v) => [v.id, v.facture_usine?.numero ?? ""] as const)),
    [ventes],
  );

  if (niveau !== "admin") {
    return (
      <div className="pad">
        <Empty
          titre="Réservé à la direction"
          detail="L'émission des factures de commission relève de la comptabilité."
        />
      </div>
    );
  }
  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  const aFacturer = commissions.filter((c) => c.etat === "a_facturer");
  const emises = commissions.filter((c) => c.fac_omnix);
  const attente = emises.filter((c) => c.etat === "facturee");
  const reglees = emises.filter((c) => c.etat === "payee");
  const partsDues = emises.filter((c) => Number(c.agent_montant) > 0 && !c.fac_agent);
  const somme = (l: Commission[]) => l.reduce((s, c) => s + Number(c.montant), 0);

  async function agir(quoi: () => Promise<string>) {
    if (occupe) return;
    setOccupe(true);
    try {
      toast(await quoi());
      recharger();
      setSerie(await lireSerie());
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'opération a échoué.");
    } finally {
      setOccupe(false);
    }
  }

  return (
    <div className="pad">
      <Kpis>
        <Kpi
          k="À facturer"
          v={euroCourt(somme(aFacturer))}
          n={`${aFacturer.length} commission${aFacturer.length > 1 ? "s" : ""} prête${aFacturer.length > 1 ? "s" : ""}`}
        />
        <Kpi
          k="Facturé, non réglé"
          v={euroCourt(somme(attente))}
          n={`${attente.length} facture${attente.length > 1 ? "s" : ""} en attente`}
        />
        <Kpi
          k="Encaissé"
          v={euroCourt(somme(reglees))}
          n={serie ? `prochain n° ${numeroSuivant(serie)}` : "série en cours de lecture"}
        />
      </Kpis>

      <Section
        titre="À facturer"
        note="commissions dont la vente est réglée"
        droite={
          aFacturer.length ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={occupe}
              onClick={() =>
                void agir(async () => {
                  const n = await emettreToutes(aFacturer);
                  return `${n} facture${n > 1 ? "s" : ""} de commission émise${n > 1 ? "s" : ""}.`;
                })
              }
            >
              Tout émettre ({aFacturer.length})
            </button>
          ) : null
        }
      >
        {aFacturer.length === 0 ? (
          <div className="card">
            <Empty titre="Rien à facturer" detail="Aucune commission n'attend sa facture Omnix." />
          </div>
        ) : (
          <Table
            lignes={aFacturer}
            cle={(c) => c.id}
            colonnes={[
              { cle: "reference", titre: "Commission", type: "ref",
                sous: (c) => nomUsine.get(c.usine_id) ?? "—" },
              { cle: "usine_id", titre: "Client de la facture", court: "Client", filtre: "select",
                mobile: false, texte: (c) => nomUsine.get(c.usine_id) ?? "—" },
              { cle: "vente_id", titre: "Vente", type: "ref", mobile: false,
                texte: (c) => refVente.get(c.vente_id) ?? "—",
                rendu: (c) => (
                  <button
                    type="button"
                    className="auth-lien"
                    onClick={() => navigue(`/ventes/${c.vente_id}`)}
                  >
                    {refVente.get(c.vente_id) ?? "—"}
                  </button>
                ) },
              { cle: "montant", titre: "Montant HT", court: "Montant", type: "euro", total: true,
                nombre: (c) => Number(c.montant) },
              {
                cle: "action",
                titre: "",
                rendu: (c) => (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={occupe}
                    onClick={() =>
                      void agir(async () => {
                        const n = await emettreFacture(c);
                        return `Facture ${n} émise à ${nomUsine.get(c.usine_id) ?? "l'usine"}.`;
                      })
                    }
                  >
                    Émettre
                  </button>
                ),
              },
            ]}
          />
        )}
      </Section>

      <Section
        titre="Parts agents à recevoir"
        note="l'agent facture sa part à Omnix, jamais à l'usine"
      >
        {partsDues.length === 0 ? (
          <div className="card">
            <Empty titre="Rien à recevoir" detail="Aucune part d'agent n'attend sa facture." />
          </div>
        ) : (
          <Table
            lignes={partsDues}
            cle={(c) => c.id}
            colonnes={[
              { cle: "agent_id", titre: "Agent", filtre: "select",
                texte: (c) => nomAgent.get(c.agent_id) ?? "—",
                sous: (c) => `${c.reference} · ${euroCourt(Number(c.montant))} encaissés` },
              { cle: "reference", titre: "Commission", type: "ref", mobile: false },
              { cle: "fac_omnix", titre: "Facture Omnix", type: "ref", mobile: false,
                texte: (c) => c.fac_omnix?.numero ?? "—" },
              { cle: "montant", titre: "Commission encaissée", court: "Encaissé", type: "euro",
                total: true, mobile: false, nombre: (c) => Number(c.montant) },
              { cle: "agent_montant", titre: "Part à reverser", court: "À reverser", type: "euro",
                total: true, nombre: (c) => Number(c.agent_montant) },
              { cle: "omnix_montant", titre: "Reste à Omnix", court: "Omnix", type: "euro",
                total: true, mobile: false, nombre: (c) => Number(c.omnix_montant) },
              {
                cle: "action",
                titre: "",
                rendu: (c) => (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={occupe}
                    onClick={() =>
                      void agir(async () => {
                        await recevoirFactureAgent(c);
                        return `Facture de ${nomAgent.get(c.agent_id) ?? "l'agent"} enregistrée : ${euro(Number(c.agent_montant))}.`;
                      })
                    }
                  >
                    <span className="t-long">Facture reçue</span>
                    <span className="t-court">Reçue</span>
                  </button>
                ),
              },
            ]}
          />
        )}
      </Section>

      <Section titre="Factures de commission" note="émises depuis cette application">
        {emises.length === 0 ? (
          <div className="card">
            <Empty
              titre="Aucune facture émise"
              detail={chargement ? "chargement…" : "Les factures de commission apparaîtront ici."}
            />
          </div>
        ) : (
          <Table
            lignes={emises}
            cle={(c) => c.id}
            chercher="Numéro de facture"
            onClic={(c, ev) => {
              if (!dansUnChamp(ev)) navigue(`/ventes/${c.vente_id}`);
            }}
            colonnes={[
              { cle: "fac_omnix", titre: "Facture", type: "ref", filtre: "texte",
                texte: (c) => c.fac_omnix?.numero ?? "—",
                sous: (c) => `${nomUsine.get(c.usine_id) ?? "—"} · ${c.reference}` },
              { cle: "date", titre: "Date", type: "date", mobile: false,
                texte: (c) => c.fac_omnix?.date ?? "",
                rendu: (c) => <span className="mono">{dateFr(c.fac_omnix?.date)}</span> },
              { cle: "usine_id", titre: "Client", filtre: "select", mobile: false,
                texte: (c) => nomUsine.get(c.usine_id) ?? "—" },
              { cle: "facture_usine", titre: "Sur facture usine", court: "Facture", type: "ref",
                mobile: false, texte: (c) => factureUsineDe.get(c.vente_id) || "—" },
              { cle: "montant", titre: "Montant HT", court: "Montant", type: "euro", total: true,
                nombre: (c) => Number(c.montant) },
              {
                cle: "etat",
                titre: "Règlement",
                filtre: "select",
                texte: (c) => (c.etat === "payee" ? "Réglée" : "En attente"),
                rendu: (c) =>
                  c.etat === "payee" ? (
                    <Tag ton="ok" dot>réglée</Tag>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={occupe}
                      onClick={() =>
                        void agir(async () => {
                          await marquerReglee(c);
                          return `${c.fac_omnix?.numero ?? c.reference} marquée réglée.`;
                        })
                      }
                    >
                      <span className="t-long">Marquer réglée</span>
                      <span className="t-court">Réglée</span>
                    </button>
                  ),
              },
            ]}
          />
        )}
      </Section>
    </div>
  );
}
